import { Col, Row, Space, Typography } from 'antd';
import React from 'react';
import PropTypes from 'prop-types';

import ExplorePanel from 'src/components/project/explore/ExplorePanel';
import {
  BoxColumn,
  useSummaryData,
  ViabilityIconBox
} from 'src/components/project/explore/summary/helpers';
import useLoadingStateClass from 'src/hooks/useLoadingStateClass';
import { useConciergeContextState } from 'src/components/concierge/ConciergeContext';
import { MetaPropertyName } from 'src/hooks/useMetaProperty';

const recordsCountString = (recordsCount, suppliersCount) =>
  `${recordsCount} record${
    recordsCount === 1 ? '' : 's'
  } from ${suppliersCount} supplier${suppliersCount === 1 ? '' : 's'}`;

const allRecordsForBreakdownId = (data, breakdownId) =>
  data?.supplier_breakdown
    ?.filter(({ id }) => id === breakdownId)
    .reduce(
      (suppliersCount, breakdownData) =>
        suppliersCount + breakdownData.record_count,
      0
    );

const allSuppliersForBreakdownId = (data, breakdownId) =>
  data?.supplier_breakdown
    ?.filter(({ id }) => id === breakdownId)
    .reduce(
      (suppliersCount, breakdownData) =>
        suppliersCount + breakdownData.supplier_count,
      0
    );

export default function PropertySummary({ compact, property }) {
  const [values] = useConciergeContextState([
    'explore',
    'filters',
    'properties',
    property
  ]);

  const { data, isLoading } = useSummaryData({
    breakdownFilters: values?.map((value) => ({
      id: value,
      properties: { [property]: [value] }
    })),
    queryProps: {
      enabled: values?.length > 0
    }
  });

  const loadingClass = useLoadingStateClass(isLoading);

  return (
    <ExplorePanel className={loadingClass} pad>
      <Space direction="vertical" size={18}>
        <Row gutter={[16, 0]}>
          <Col style={{ marginRight: 'auto' }}>
            <Typography.Text strong>Grades</Typography.Text>
          </Col>
          <BoxColumn>
            <Typography.Text strong>Availability</Typography.Text>
          </BoxColumn>
        </Row>
        {values.map((value) => {
          const supplierCount = allSuppliersForBreakdownId(data, value);
          const recordCount = allRecordsForBreakdownId(data, value);
          return (
            <Row
              className="explore-summary--kpi-row"
              gutter={[16, 0]}
              align="middle"
              key={value}
            >
              <Col style={{ marginRight: 'auto' }}>
                <Typography.Text strong>
                  {value ? (
                    <MetaPropertyName property={property} optionValue={value} />
                  ) : (
                    <>
                      (no <MetaPropertyName property={property} lowercase />)
                    </>
                  )}
                </Typography.Text>
                <br />
                <Typography.Text type="secondary">
                  {supplierCount} Suppliers
                </Typography.Text>
              </Col>
              <BoxColumn>
                <ViabilityIconBox
                  recordsCount={recordCount}
                  title={recordsCountString(recordCount, supplierCount)}
                />
              </BoxColumn>
            </Row>
          );
        })}
      </Space>
    </ExplorePanel>
  );
}
PropertySummary.propTypes = {
  compact: PropTypes.bool,
  property: PropTypes.string.isRequired
};
